import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { isPermitted, useJWT } from 'src/app/common/utils';
import { map } from 'lodash';
import { LayoutSplashScreen } from 'src/app/layout';
import { MasterDataSettingValueItem, ProductField } from '../../../../types/master-data-setting-types';
import {
  ContractDataFormMode,
  CreateUpdateContractSettingBody,
  ContractSettingFormValues,
  ContractSettingItem,
} from '../../../../types/contract-setting-types';
import { createContractSettingItem, modifyContractSettingItem } from '../../../../network/contract-setting-crud';
import { fetchMasterDateSettingItemByName } from '../../../../network/master-data-setting-crud';
import { ContractSettingFieldDefinitions, AdminCommissionToolPermissionType } from '../../../../constants';
import { contractSettingPath } from '../../contract-setting-routes';
import { useTranslation } from 'src/app/common/hook/translation.hook';
import {
  sanitizeCommissionRate,
  createInitialFormState,
  hasCommissionRateError as checkCommissionRateError,
  hasMasterDataError as checkMasterDataError,
  getCommissionRateInputValue,
  buildInitialOptions,
  normalizeCommissionRateInput,
} from '../../../../utils/contract-setting-validation';

type ContractSettingFormProps = {
  formMode: ContractDataFormMode;
  contractSettingItem?: ContractSettingItem;
};

const FIELD_CONTAINER_WIDTH = 220;
const COMMISSION_RATE_INPUT_PATTERN = /^\d+(\.\d{0,2})?$/;

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  rowSpacing: {
    marginBottom: 16,
  },
  fieldContainer: {
    width: FIELD_CONTAINER_WIDTH,
    boxSizing: 'border-box',
    height: 50,
    display: 'flex',
    alignItems: 'center',
  },
  field: {
    fontSize: '1rem',
  },
  mandatory: {
    color: 'red',
  },
  sectionMargin: {
    marginBottom: 15,
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
  },
}));

const ContractSettingForm: FC<ContractSettingFormProps> = ({ formMode, contractSettingItem }) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const Translation = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const isCreateMode = formMode === ContractDataFormMode.CREATE;

  const [formState, setFormState] = useState<ContractSettingFormValues>(createInitialFormState(contractSettingItem));
  const [commissionRateInput, setCommissionRateInput] = useState(
    getCommissionRateInputValue(contractSettingItem?.commissionRate),
  );
  const [masterDataOptions, setMasterDataOptions] = useState<
    Partial<Record<ProductField, MasterDataSettingValueItem[]>>
  >(contractSettingItem ? buildInitialOptions(contractSettingItem) : {});
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  let createPermission = false;
  let updatePermission = false;
  const jwt = useJWT() || {};

  if (jwt.permissions) {
    const jwtPermission = map(jwt.permissions, (_, key) => key);
    createPermission = isPermitted([AdminCommissionToolPermissionType.CONTRACT_SETTING_CREATE], jwtPermission);
    updatePermission = isPermitted([AdminCommissionToolPermissionType.CONTRACT_SETTING_UPDATE], jwtPermission);
  }

  const hasPermission = (isCreateMode && createPermission) || (!isCreateMode && updatePermission);
  if (!hasPermission) {
    history.push(`${contractSettingPath}/list`);
  }

  useEffect(() => {
    const nextFormState = createInitialFormState(contractSettingItem);

    setFormState(nextFormState);
    setCommissionRateInput(getCommissionRateInputValue(nextFormState.commissionRate));
    if (contractSettingItem) {
      setMasterDataOptions(buildInitialOptions(contractSettingItem));
    }

    // Prefetch all field options in parallel
    setIsLoadingOptions(true);
    const fetchPromises = ContractSettingFieldDefinitions.map(({ masterDataName }) =>
      fetchMasterDateSettingItemByName(masterDataName, dispatch).then((result) => {
        setMasterDataOptions((prev) => ({ ...prev, [masterDataName]: result.values }));
      }),
    );
    Promise.all(fetchPromises).finally(() => setIsLoadingOptions(false));
  }, [contractSettingItem]);

  const hasCommissionRateError = checkCommissionRateError(formState.commissionRate);
  const hasMasterDataError = checkMasterDataError(formState);

  const onSubmitConfirm = () => {
    setIsSubmitAttempted(true);
    if (hasMasterDataError || hasCommissionRateError) {
      return;
    }
    setConfirmOpen(true);
  };

  const onSubmit = async () => {
    setConfirmOpen(false);
    try {
      const payload: CreateUpdateContractSettingBody = {
        productNameId: formState.productNameId?._id || '',
        insurancePlanId: formState.insurancePlanId?._id || '',
        insurancePremiumId: formState.insurancePremiumId?._id || '',
        premiumPaymentTermId: formState.premiumPaymentTermId?._id || '',
        premiumPaymentFrequencyId: formState.premiumPaymentFrequencyId?._id || '',
        premiumPaymentYearId: formState.premiumPaymentYearId?._id || '',
        effectiveDateId: formState.effectiveDateId?._id || '',
        riderCountId: formState.riderCountId?._id || '',
        commissionRate: formState.commissionRate,
      };

      if (isCreateMode) {
        await createContractSettingItem(payload, dispatch);
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: Translation('master.data.setting.update.successNotName'),
            },
          ]),
        );
      } else if (contractSettingItem) {
        await modifyContractSettingItem(contractSettingItem._id, payload, dispatch);
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: Translation('master.data.setting.update.successNotName'),
            },
          ]),
        );
      }
      history.push(`${contractSettingPath}/list`);
    } catch (err) {}
  };

  if (isLoadingOptions) {
    return <LayoutSplashScreen />;
  }

  return (
    <>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle align="center">{Translation('contract.setting.confirm')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{Translation('contract.setting.submit.confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" onClick={() => setConfirmOpen(false)}>
            {Translation('app.button.cancel')}
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            {Translation('app.button.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.rowContainer}>
            <div className={commonClasses.header}>
              {Translation(isCreateMode ? 'contract.setting.create' : 'contract.setting.edit')}
            </div>
          </div>
          <Button variant="contained" color="inherit" onClick={() => history.push(`${contractSettingPath}/list`)}>
            {Translation('app.button.back')}
          </Button>
        </div>

        <div className={classes.sectionMargin}>
          {ContractSettingFieldDefinitions.map(({ fieldKey, masterDataName, labelKey }) => {
            const options = masterDataOptions[masterDataName] || [];
            const currentOption = formState[fieldKey];
            const currentValue = currentOption?._id || '';
            const hasCurrentValueInOptions = !currentValue || options.some((option) => option._id === currentValue);
            const hasError = isSubmitAttempted && !currentOption?._id;

            return (
              <div key={masterDataName} className={`${classes.rowContainer} ${classes.rowSpacing}`}>
                <div className={classes.fieldContainer}>
                  <span className={classes.field}>
                    {Translation(labelKey)}
                    <span className={classes.mandatory}>*</span>
                  </span>
                </div>
                <div style={{ flexGrow: 1 }}>
                  <FormControl error={hasError} margin="dense" variant="outlined" fullWidth>
                    <Select
                      displayEmpty
                      value={currentValue}
                      onChange={(event) => {
                        const nextOption = options.find((option) => option._id === event.target.value);

                        setFormState((prev) => ({
                          ...prev,
                          [fieldKey]: nextOption,
                        }));
                      }}
                    >
                      {!hasCurrentValueInOptions && (
                        <MenuItem value={currentValue} disabled style={{ display: 'none' }}>
                          {currentOption?.value || currentValue}
                        </MenuItem>
                      )}
                      {options.map((option) => (
                        <MenuItem key={option._id} value={option._id}>
                          {option.value}
                        </MenuItem>
                      ))}
                    </Select>
                    {hasError && (
                      <FormHelperText style={{ color: '#f018a6' }}>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              </div>
            );
          })}

          <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation('contract.setting.commissionRate')}%<span className={classes.mandatory}>*</span>
              </span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <TextField
                type="text"
                margin="dense"
                variant="outlined"
                autoComplete="off"
                fullWidth
                placeholder={Translation('master.data.setting.pleaseInput')}
                value={commissionRateInput}
                onBlur={() => {
                  if (commissionRateInput.endsWith('.')) {
                    setCommissionRateInput(commissionRateInput.slice(0, -1));
                  }
                }}
                onChange={(e) => {
                  const rawValue = e.target.value;

                  if (!rawValue.length) {
                    setCommissionRateInput('');
                    setFormState((prev) => ({ ...prev, commissionRate: undefined }));
                    return;
                  }

                  if (!COMMISSION_RATE_INPUT_PATTERN.test(rawValue)) {
                    return;
                  }

                  const normalizedInput = normalizeCommissionRateInput(rawValue);
                  const nextValue = sanitizeCommissionRate(Number(normalizedInput));

                  if (nextValue === undefined) {
                    return;
                  }

                  setCommissionRateInput(normalizedInput);
                  setFormState((prev) => ({ ...prev, commissionRate: nextValue }));
                }}
                error={isSubmitAttempted && hasCommissionRateError}
                helperText={isSubmitAttempted && hasCommissionRateError ? MANDATORY_FIELD_ERROR_TEXT : ''}
                inputProps={{ inputMode: 'decimal' }}
                FormHelperTextProps={{ component: 'div' }}
              />
            </div>
          </div>
        </div>

        <div className={classes.footerContainer}>
          <Button variant="contained" color="secondary" onClick={onSubmitConfirm}>
            {Translation('app.button.submit')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ContractSettingForm;
